import {
  debounce,
  FormControl,
  FormControlProps,
  StackProps,
  SxProps,
  Theme,
  useTheme,
  useThemeProps,
} from '@mui/material';
import { FORM_FIELD_THEME_NAME } from '@onevision-resources/design-theme';
import { Editor, EditorContentProps, Highlight } from '@provision/rich-text-editor';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  ControllerRenderProps,
  type UseControllerProps,
  useFormContext,
} from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { RichTextField } from 'src/components/Forms/RichTextField/rich-text-field';
import { getRawTextFromHTML, hasImgTags } from 'src/utils/text-parsing';

export type FormRichTextBubbleProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'onChange'
> & {
  label?: string;
  placeholder?: string;
  required?: boolean | string;
  rules?: UseControllerProps['rules'];
  disabled?: boolean;
  editable?: boolean;
  value?: string;
  dependencies?: React.DependencyList;
  className?: string;
  debounced?: number;
  watchContent?: boolean;
  onChange?: (value: string) => void;
  richTextStyles?: SxProps<Theme>;
} & { editorContainerProps?: Partial<StackProps> } & {
  editorContentProps?: Omit<Partial<EditorContentProps>, 'editor' | 'ref'>;
  editorMaxHeight?: string | number | null;
} & CommonFormFieldProps<'rich-text-bubble', string>;

export const FormRichTextBubble = (inProps: FormRichTextBubbleProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    required,
    rules,
    children,
    editorContainerProps,
    editorContentProps,
    label,
    editable = true,
    disabled = false,
    dependencies,
    className,
    placeholder,
    value,
    debounced,
    watchContent,
    onChange,
    editorMaxHeight = '15rem',
    richTextStyles,
    labelAbove = false,
    size,
    ...rest
  } = props;
  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;

  const theme = useTheme();
  const latestFieldRef = useRef<ControllerRenderProps | null>(null);
  const latestFieldValueRef = useRef<string | string[] | undefined>(undefined);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleUpdate = useCallback(({ editor }: { editor: Editor }) => {
    const field = latestFieldRef.current;
    if (!field) {
      return;
    }

    const fieldContent = editor?.getHTML().trim() || '';

    if (latestFieldValueRef.current !== fieldContent) {
      field.onChange(fieldContent);
      latestFieldValueRef.current = fieldContent;
      onChangeRef.current?.(fieldContent);
    }
  }, []);

  const debouncedOnUpdate = useMemo(() => {
    if (!debounced) {
      return null;
    }

    return debounce(handleUpdate, debounced);
  }, [debounced, handleUpdate]);

  useEffect(
    () => () => {
      debouncedOnUpdate?.clear?.();
    },
    [debouncedOnUpdate],
  );

  const onSubmit = useCallback(
    (field: ControllerRenderProps) => (editorText: string) => {
      if (getRawTextFromHTML(editorText) || hasImgTags(editorText)) {
        latestFieldValueRef.current = editorText;
        field.onChange(editorText);
      }
    },
    [],
  );

  return (
    <FormControl variant="standard" size={size} {...rest} error={!!errorMsg}>
      <Controller
        name={name}
        control={control}
        rules={{ required, ...rules }}
        render={({ field }) => {
          latestFieldRef.current = field;
          latestFieldValueRef.current = field.value as string | string[] | undefined;

          const editorContent =
            value ??
            ((Array.isArray(field.value) ? field.value[0] : field.value) as string) ??
            '';
          const onUpdate = debouncedOnUpdate ?? handleUpdate;

          const editorMaxHeightStyles =
            editorMaxHeight === null
              ? { maxHeight: 'none', overflow: 'visible' }
              : { maxHeight: editorMaxHeight };

          return (
            <RichTextField
              key={theme.palette.mode}
              error={errorMsg?.toString()}
              label={label}
              labelAbove={labelAbove}
              size={size}
              placeholder={placeholder}
              watchContent={watchContent}
              sx={{
                // TODO: Refactor `chat-utils` package to provide a better way
                // to style rich text editor.
                '& .portal-editor': {
                  ...editorMaxHeightStyles,
                },
                ...richTextStyles,
              }}
              onBlur={(_, editor) => {
                const fieldContent = editor?.getHTML().trim() || '';
                if (field.value === fieldContent) {
                  return;
                }

                field.onBlur();

                const actualTextWithoutMarkup = getRawTextFromHTML(fieldContent);
                if (actualTextWithoutMarkup) {
                  field.onChange(fieldContent);
                  onChange?.(fieldContent);
                  latestFieldValueRef.current = fieldContent;
                }
              }}
              onSubmit={onSubmit(field)}
              disabled={disabled}
              editorOptions={{
                editable: !!editable && !disabled,
                extensions: [
                  Highlight.configure({
                    styles: {
                      'background-color':
                        theme.palette.custom.OV.sys.aiSuggestionsTextBackground,
                      'border-radius': '4px',
                      padding: '0 1px',
                      color: 'inherit',
                    },
                  }),
                ],
                content: editorContent,
                onUpdate,
              }}
              dependencies={dependencies}
              className={className}
              editorContentProps={{
                ...editorContentProps,

                innerRef: field.ref,
              }}
            />
          );
        }}
      />
    </FormControl>
  );
};
